import { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseConfig';
import Navbar from './components/navbar.jsx';
import Footer from './components/Footer.jsx';
import ScrollToTop from './components/ScrollToTop.jsx';
import GlobalErrorModal from './components/GlobalErrorModal.jsx';
import TokenGate from './components/TokenGate.jsx';

const Home = lazy(() => import('./pages/home.jsx'));
const Search = lazy(() => import('./pages/search.jsx'));
const Login = lazy(() => import('./pages/Login.jsx'));
const Register = lazy(() => import('./pages/Register.jsx'));
const MyList = lazy(() => import('./pages/MyList.jsx'));
const Profile = lazy(() => import('./pages/Profile.jsx'));
const ResetPassword = lazy(() => import('./pages/ResetPassword.jsx'));
const Details = lazy(() => import('./pages/Details.jsx'));
const NotFound = lazy(() => import('./pages/NotFound.jsx'));

const AppLoader = () => (
    <div className="app-centered">
        Caricamento...
    </div>
);

export const ProtectedRoute = ({ loading, isAuthenticated }) => {
    if (loading) {
        return <AppLoader />;
    }
    return isAuthenticated ? <Outlet /> : <Navigate to="/login" />;
};

export const PublicRoute = ({ loading, isAuthenticated }) => {
    if (loading) {
        return <AppLoader />;
    }
    return isAuthenticated ? <Navigate to="/" /> : <Outlet />;
};

export default function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const appContent = (
        <>
            <GlobalErrorModal />
            <ScrollToTop />
            {isAuthenticated && <Navbar />}
            <Suspense fallback={<AppLoader />}>
                <Routes>
                    <Route element={<PublicRoute loading={loading} isAuthenticated={isAuthenticated} />}>
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                    </Route>
                    <Route element={<ProtectedRoute loading={loading} isAuthenticated={isAuthenticated} />}>
                        <Route path='/' element={<Home />} />
                        <Route path="/home" element={<Home />} />
                        <Route path="/search" element={<Search />} />
                        <Route path="/mylist" element={<MyList />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path='/details/:type/:id' element={<Details />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
            {isAuthenticated && <Footer />}
        </>
    );

    return (
        <BrowserRouter>
            <TokenGate>{appContent}</TokenGate>
        </BrowserRouter>
    );
}
